import { hasConsent, onConsentChange } from '@shared/consent';
import { isDatedAhead } from '@shared/lib/storedTime';

import { dataLayer } from './consentMode';
import { measurementOn } from './market';

/**
 * DIT-513 — the two events the funnel reports, and the rules on when they go out.
 *
 * `signup_submitted` and `account_created`. There is no browser `purchase`: since
 * no-card onboarding the sign-up ends at verification, nothing in the funnel
 * reaches a payment step, and the purchase is reported server-side by the API,
 * once per subscription.
 *
 * **Pushed whatever the visitor has answered — Consent Mode's advanced mode.** The
 * client chose it on 16 September. Google's tags read the consent state the page
 * has pushed and, where it is denied, send a ping without cookies instead of a
 * measured hit, from which Google models the conversions of visitors who refuse.
 * An earlier version held each event until the banner was answered and dropped it
 * on a refusal, which is what basic mode needed and leaves advanced mode nothing to
 * model. ⚠ It also means these events are in the dataLayer before any answer, so a
 * tag that does not read Consent Mode — the Meta pixel, for one — must not fire on
 * them unconditionally: a conversion tag on these events needs
 * `consent_marketing` = `granted` (pushed with `di_consent_update`) as a condition
 * of its trigger. That rule is the container's, and
 * docs/integrations/google-analytics.md records it.
 *
 * **What waits for consent is what identifies or stays.** Before a measurement
 * category is accepted an event carries its name and what describes the purchase
 * decision — `plan_id`, `seats` — and nothing that names somebody: `company_id`
 * needs Analytics or Advertising, the e-mail hash needs Advertising, both as
 * accepted at the moment the event is pushed. An event pushed before the answer
 * never gains them afterwards, because pushing it again to add them would count
 * the conversion twice. And nothing reaches storage without a measurement
 * category — see `remember`.
 *
 * The parameter shapes come from the client: `seats` is a number, and a field the
 * server did not send is left out rather than sent as zero.
 */

const ONCE_PREFIX = 'di_evt_';

/** How long a once-only key is remembered — the attribution window, not forever. */
export const ONCE_TTL_MS = 90 * 24 * 60 * 60 * 1000;

interface Report {
  event: string;
  params: Record<string, unknown>;
  /**
   * Parameters that identify an account, added only if Analytics is accepted when
   * the event is delivered. The event itself goes out whatever the answer (Consent
   * Mode advanced), and in advanced mode Google's analytics tags still send a hit
   * without cookies for a visitor who refused Analytics — so a stable identifier
   * that only the analytics side reads (GA4's custom dimension) goes with Analytics
   * and with nothing else, not with Advertising alone.
   */
  analyticsParams?: Record<string, unknown>;
  /**
   * Parameters that exist only for advertising, added only if Advertising is
   * accepted when the event is delivered — so neither a visitor without consent
   * nor the analytics tags of one with Analytics alone can read what only the
   * advertising ones use.
   */
  advertisingParams?: Record<string, unknown>;
  /** Present for an event that must not repeat from this browser. */
  onceKey?: string;
}

/**
 * Once-only keys spent while this page is open, beside storage. It is what stops
 * this page reporting one thing twice whatever the visitor answered — the
 * back-and-resubmit `signup_submitted` guards against happens without a reload —
 * and, without consent, the only record there is. Memory only, and kept on a
 * withdrawal: the events keep going out, so forgetting would push the same one
 * again.
 */
const spentThisPage = new Map<string, number>();

/** Inside the window, and not dated so far ahead of the clock that it would never expire. */
function isCurrent(at: number | undefined): boolean {
  if (!at) return false;
  return Date.now() - at <= ONCE_TTL_MS && !isDatedAhead(at);
}

/** Whether anything may be measured: either category the tags run under. */
function measurementAllowed(): boolean {
  return hasConsent('analytics') || hasConsent('marketing');
}

/**
 * Guarded, because the queue is not ours alone: another script can replace
 * `window.dataLayer` with something whose `push` throws, and measurement must
 * never cost the page the step it was measuring.
 */
function push(event: string, params: Record<string, unknown>): boolean {
  try {
    const queue = dataLayer();
    if (!queue) return false;
    queue.push({ event, ...params });
    return true;
  } catch {
    return false;
  }
}

/**
 * Has this exact thing already been reported from this browser, recently?
 *
 * `localStorage`, because `account_created` is reachable by re-opening a link and
 * a reload has to be enough to remember. A key older than the window, or one
 * written by the first version of this module (which stored `'1'` and no time),
 * counts as not reported and is removed, as does one dated more than a day ahead
 * of the clock. A key spent while this page is open is also held in memory, for
 * storage that refuses it.
 */
function alreadyReported(key: string): boolean {
  if (isCurrent(spentThisPage.get(key))) return true;
  const full = `${ONCE_PREFIX}${key}`;
  try {
    if (isCurrent(Number(window.localStorage.getItem(full)))) return true;
    window.localStorage.removeItem(full);
    return false;
  } catch {
    // Blocked storage: this page's memory still remembers, and after a reload the
    // event may repeat, which is the honest failure.
    return false;
  }
}

function store(key: string, at: number): void {
  try {
    window.localStorage.setItem(`${ONCE_PREFIX}${key}`, String(at));
  } catch {
    /* see alreadyReported() */
  }
}

/**
 * Spend a key: in this page's memory always, in storage only with consent to
 * measure. Without consent a reload can therefore report the same thing again —
 * the price of storing nothing for a visitor who has not agreed to it. A grant
 * later writes what this page has spent (`startEventConsentHandling`).
 */
function remember(key: string): void {
  const at = Date.now();
  spentThisPage.set(key, at);
  if (measurementAllowed()) store(key, at);
}

function removeReportedEvents(shouldRemove: (value: string | null) => boolean): void {
  try {
    const storage = window.localStorage;
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key?.startsWith(ONCE_PREFIX) && shouldRemove(storage.getItem(key))) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch {
    /* see alreadyReported() */
  }
}

/**
 * Remove every stored once-only key — consent to measure has been refused or
 * withdrawn. This page's memory stays: see `spentThisPage`.
 */
export function forgetReportedEvents(): void {
  removeReportedEvents(() => true);
}

/**
 * Remove the once-only keys past their window, any without a time, and any dated
 * ahead of the clock.
 *
 * Reading an old key as absent is not the same as removing it: a key is only
 * checked again when the same event comes round for the same company or address,
 * which for most never happens, so without this they stayed for as long as the
 * visitor kept measurement on — past the ninety days the banner states.
 */
export function sweepExpiredReportedEvents(): void {
  removeReportedEvents((value) => !isCurrent(Number(value)));
}

function deliver(report: Report): boolean {
  if (report.onceKey && alreadyReported(report.onceKey)) return false;
  const pushed = push(report.event, {
    ...report.params,
    ...(hasConsent('analytics') ? report.analyticsParams : {}),
    ...(hasConsent('marketing') ? report.advertisingParams : {}),
  });
  // Remembered only once it has actually gone: a push that failed must not spend
  // the only chance the event had.
  if (pushed && report.onceKey) remember(report.onceKey);
  return pushed;
}

/**
 * Deliver now, whatever the banner's answer — see the module docstring. Nothing on a
 * market where measurement is not switched on: no event, and no once-only key spent,
 * so the same sign-up is reported once the market is switched on.
 */
function send(report: Report): boolean {
  if (typeof window === 'undefined' || !measurementOn()) return false;
  return deliver(report);
}

/**
 * Keep the stored once-only keys in step with the answer. Call once, at boot.
 *
 * A grant writes the keys this page has already spent, so a reload after accepting
 * does not report them a second time. A refusal or a withdrawal removes the stored
 * keys: they are storage kept for measurement, and a visitor who says no to
 * measurement keeps none of it. The expired ones go first, whatever the answer.
 */
export function startEventConsentHandling(): void {
  sweepExpiredReportedEvents();
  onConsentChange(() => {
    if (measurementAllowed()) {
      spentThisPage.forEach((at, key) => {
        if (isCurrent(at)) store(key, at);
      });
    } else {
      forgetReportedEvents();
    }
  });
}

/**
 * A short stable fingerprint of an address, for a once-only key — so the address
 * itself is never what storage holds. FNV-1a: it only has to tell one address
 * from another within one browser, and is not asked to keep a secret.
 */
function fingerprint(value: string): string {
  let hash = 0x811c9dc5;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
}

/**
 * The form was submitted — after the server accepted it, not on the click.
 *
 * This is the event campaigns optimise on at launch, because its attribution is
 * reliable: the advertising cookies are in *this* browser. A submission that comes
 * back 400 is not a submitted signup.
 *
 * **Once per address per browser.** A visitor who goes back and resubmits the same
 * address would otherwise inflate exactly the number campaigns bid on. The key is
 * spent only when the event actually goes out, like every once-only key here — and
 * it always goes out now, so a submission made while measurement is refused is
 * reported then, without cookies. Accepted later on the same page, it is not
 * reported again. Submitted again after a reload without consent nothing was stored
 * here to remember it by — the server's `resubmitted` answer covers that case, see
 * `reportSignupSubmitted`.
 */
export function signupSubmitted(email: string): boolean {
  const address = email.trim().toLowerCase();
  if (!address) return false;
  return send({
    event: 'signup_submitted',
    onceKey: `signup_submitted:${fingerprint(address)}`,
    params: {},
  });
}

export interface AccountCreatedParams {
  companyId: string;
  /** Absent means the server did not say, and the parameter is left out. */
  planId?: string;
  /** Only when the funnel chose a seat count; the server leaves it out otherwise. */
  seats?: number;
  /**
   * SHA-256 of the lowercased, trimmed e-mail, computed by the server — the one
   * field manual Advanced Matching sends. Never hashed here: the verification link
   * is routinely opened in a browser that does not know the address.
   */
  emailSha256?: string;
}

/**
 * A real account exists, confirmed by the server. Once per company per browser.
 *
 * Keyed on the company rather than on the link: the same company opened from two
 * links is still one account. A second browser is handled before this is called —
 * the server marks that response `resumed`, and `reportAccountCreated` drops it.
 */
export function accountCreated(params: AccountCreatedParams): boolean {
  if (!params.companyId) return false;
  return send({
    event: 'account_created',
    onceKey: `account_created:${params.companyId}`,
    params: {
      ...(params.planId ? { plan_id: params.planId } : {}),
      ...(params.seats !== undefined ? { seats: params.seats } : {}),
    },
    analyticsParams: { company_id: params.companyId },
    // Only advertising matching uses the hash (Google's enhanced conversions, Meta's
    // manual matching), so Analytics alone does not put it where every tag can read it.
    ...(params.emailSha256 ? { advertisingParams: { email_sha256: params.emailSha256 } } : {}),
  });
}
